import { BadgeDollarSign, BadgeCheck, Eye, Gem, Ruler, Scissors, Search } from "lucide-react";
import GuideTemplate from "@/pages/GuideTemplate";

const CFG = {
  seo: {
    testid: "diamond-guide",
    path: "/diamond-buying-guide",
    image: "/assets/diamond-guide-hero.jpg",
    title: "How to Buy a Diamond Without Overpaying — The 4Cs Explained | Gold Time Jewelry",
    description:
      "A Texas family jeweler's honest diamond buying guide: the 4Cs ranked by what actually matters, GIA vs IGI certification, and where to spend vs save. Gold Time Jewelry — Katy, Copperas Cove & Killeen TX.",
  },
  hero: {
    eyebrow: "The Diamond Buying Guide",
    title: "How to buy a diamond",
    titleAccent: "without overpaying",
    intro:
      "Two diamonds with identical grades can look wildly different — and cost it. After years of grading stones at our counters, here's the framework we use ourselves: what each of the 4Cs really does, which one never to compromise, and where the smart money saves.",
    image: "/assets/diamond-guide-hero.jpg",
    imageAlt: "Round brilliant natural diamond macro on champagne silk with light refraction",
    ctas: [
      { to: "/collections/diamond-jewelry", label: "Shop diamond jewelry" },
      { to: "/contact", label: "Ask a jeweler" },
    ],
  },
  facts: {
    title: "Graded by labs,",
    titleAccent: "judged by eyes",
    paragraphs: [
      "Our certified natural diamonds come with an independent laboratory report — GIA or IGI — graded on the 4Cs: cut, color, clarity and carat. When a stone is certified, the certificate is your protection and the report details come with the piece.",
      "But certificates don't sparkle — stones do. That's why we encourage every customer to compare diamonds side by side under our lights before deciding. The right diamond is the one that performs, not just the one that papers well.",
    ],
    items: [
      { icon: BadgeCheck, label: "GIA & IGI certified" },
      { icon: Eye, label: "Compare stones in person" },
      { icon: Search, label: "Loupe every stone with us" },
    ],
  },
  showcase: {
    title: "Carat is weight,",
    titleAccent: "not size — and not sparkle",
    paragraphs: [
      "A carat is 0.2 grams — a measure of weight, not beauty. A well-cut 0.90ct diamond can face up larger and brighter than a poorly cut 1.00ct stone, while costing meaningfully less.",
      "Prices jump at round numbers (0.50, 1.00, 1.50ct). Buying just under a magic mark — 0.93ct instead of 1.00ct — is one of the oldest insider moves in the business, and your eye will never know the difference.",
    ],
    image: "/assets/diamond-guide-4cs.jpg",
    imageAlt: "Four round diamonds of graduating carat weights in a row",
    imageFirst: false,
  },
  cards: {
    title: "The 4Cs,",
    titleAccent: "ranked honestly",
    items: [
      {
        icon: Scissors,
        title: "1. Cut — never compromise",
        text: "Cut controls the light. An Excellent-cut diamond out-sparkles a bigger, cleaner, whiter stone with a mediocre cut every single time. Spend here first.",
      },
      {
        icon: Gem,
        title: "2. Color — G to I is the value zone",
        text: "Colorless D–F commands premium prices, but G–I looks white once set — especially in yellow gold, where the metal's warmth hides a grade or two.",
      },
      {
        icon: Eye,
        title: "3. Clarity — buy eye-clean, not flawless",
        text: "VS2 and SI1 stones look identical to FL under any normal viewing — the inclusions need a microscope to find. Paying for VVS clarity is paying for a line on paper.",
      },
      {
        icon: Ruler,
        title: "4. Carat — buy shy of the mark",
        text: "0.93ct instead of 1.00ct, 1.40ct instead of 1.50ct. Same visual size, noticeably kinder price. Weight is where flexibility pays best.",
      },
    ],
  },
  table: {
    title: "Where your money",
    titleAccent: "actually goes",
    columns: ["What it means", "The sweet spot", "Where people overpay"],
    rows: [
      { label: "Cut", values: ["Proportions & finish — the engine of sparkle", "Excellent / Ideal", "Settling for 'Good' cut to gain carat size"] },
      { label: "Color", values: ["Whiteness on a D–Z scale", "G–H (I in yellow gold)", "D–F colorless grades nobody can see once set"] },
      { label: "Clarity", values: ["Freedom from inclusions, FL to I3", "VS2 / SI1 eye-clean", "VVS+ grades that need a microscope to matter"] },
      { label: "Carat", values: ["Weight: 1ct = 0.2g", "Just under round marks", "1.00ct+ 'magic numbers' with visible price cliffs"] },
    ],
  },
  honestTake: {
    title: "Our honest take",
    text: "If we were spending our own money: Excellent cut, G–H color, eye-clean VS2–SI1 clarity, and a weight just under a round number. That combination buys a stone that performs like a showpiece for thousands less than the 'all-A's' certificate hunters pay. And if size-per-dollar is the top priority, ask us to show you lab-grown side by side with natural — we'll put both in your hand and let your eyes decide.",
    image: "/assets/diamond-guide-loupe.jpg",
    imageAlt: "Jeweler examining a round diamond with a loupe and tweezers at the workbench",
  },
  faqs: [
    {
      q: "Which of the 4Cs matters most?",
      a: "Cut, by a wide margin. Cut determines how much light the stone returns to your eye — an Excellent cut makes a modest diamond look alive, while a poor cut makes an expensive one look sleepy. It's the one C we never let a customer trade away.",
    },
    {
      q: "GIA vs IGI certification — what's the difference?",
      a: "Both are respected independent labs. GIA is the stricter, longer-established grader for natural diamonds and carries a slight prestige premium; IGI grades to consistent standards and dominates lab-grown certification. We show you the certificate — and more importantly, the stone — either way.",
    },
    {
      q: "How much should I spend on a diamond?",
      a: "Ignore the old 'two months' salary' rule — that was an ad campaign. Spend what comfortably fits your life. With honest grading and smart 4C trade-offs, beautiful stones exist at nearly every budget, and we'll show you the best your number buys rather than pushing you past it.",
    },
    {
      q: "Should I buy natural or lab-grown?",
      a: "Natural diamonds carry rarity and stronger long-term value; lab-grown gives you a visibly larger or finer stone for 40–60% less. Both are real diamonds. Read our lab-grown guide, then come compare both in person — it's a five-minute decision once they're in your hand.",
    },
    {
      q: "Can I see and compare diamonds before buying?",
      a: "Please do — that's the whole advantage of a family jeweler. Visit any of our three Texas showrooms and we'll pull comparable stones, loupe them together under proper lighting, and explain exactly what you're seeing before you spend a dollar.",
    },
    {
      q: "Will you help me trade in or upgrade later?",
      a: "Yes — we buy and accept trade-ins on diamonds as well as gold. Bring the stone and any paperwork; we'll test and evaluate it in front of you and put its value toward whatever's next.",
    },
  ],
  cta: {
    heading: "See the difference in person",
    text: "Browse certified diamonds online with insured shipping — or visit a showroom and compare stones side by side under the loupe with our team.",
    bgImage: "/assets/diamond-guide-cta.jpg",
    buttons: [
      { to: "/collections/diamond-jewelry", label: "Shop certified diamonds" },
      { to: "/showrooms", label: "Visit a showroom" },
    ],
  },
  moreGuides: [
    { to: "/lab-grown-diamonds", label: "Lab-Grown Diamonds, Explained Honestly" },
    { to: "/gold-karat-guide", label: "10K vs 14K vs 18K Gold — What the Stamp Means" },
  ],
};

export default function DiamondGuidePage() {
  return <GuideTemplate cfg={CFG} />;
}
